import { Skeleton } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FieldArrayWithId,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { parameterCategories } from 'src/constants/deviceConstants';
import { getParameterDetails } from 'src/store/actions/deviceActions';
import { DeviceValue } from 'src/store/models/Device';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useResizeObserver } from 'src/utils/useResizeObserver';

import ParameterTabs from '../ParameterTabs/ParameterTabs';
import ShadowBox from '../ShadowBox/ShadowBox';
import classes from './DeviceParameters.module.scss';
import Parameter from './Parameter/Parameter';

export interface DeviceParametersProps {
  loading?: boolean;
}

const DeviceParameters: FC<DeviceParametersProps> = ({ loading = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const firstUpdate = useRef(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const hiddenRef = useRef<HTMLDivElement>(null);

  const { control, reset } = useFormContext<{ parameters: DeviceValue[] }>();
  const { fields } = useFieldArray({
    control,
    keyName: 'key',
    name: 'parameters',
  });

  const deviceDetails = useAppSelector((state) => state.device.deviceDetails);
  const parameters = useAppSelector((state) => state.device.parameters);
  const paramLoading = useLoadingSelector({
    actionType: getParameterDetails.toString(),
  });

  useEffect(() => {
    if (deviceDetails && firstUpdate.current) {
      reset({ parameters: deviceDetails?.controller.diagnostic.parameters });
      firstUpdate.current = false;
      dispatch(getParameterDetails());
    }
    // eslint-disable-next-line
  }, [deviceDetails]);

  const [currentTab, setCurrentTab] = useState(0);
  const [leftSide, setLeftSide] = useState<
    FieldArrayWithId<
      {
        parameters: DeviceValue[];
      },
      'parameters',
      'key'
    >[]
  >([]);
  const [rightSide, setRightSide] = useState<
    FieldArrayWithId<
      {
        parameters: DeviceValue[];
      },
      'parameters',
      'key'
    >[]
  >([]);

  const currParameters = useMemo(
    () =>
      fields.filter((field) =>
        parameterCategories[currentTab]?.parameters?.includes(field.id)
      ),
    [currentTab, fields]
  );

  const createColumns = useCallback(() => {
    const outer = hiddenRef?.current?.getBoundingClientRect();
    setLeftSide([]);
    setRightSide([]);
    if (outer && currParameters?.length) {
      dispatch(showLoading());
      let left: typeof leftSide = [];
      let leftHeight = 0;
      let right: typeof rightSide = [];
      let rightHeight = 0;
      currParameters?.forEach((p, i) => {
        const element = document
          .getElementById(p?.key)
          ?.getBoundingClientRect();
        if (outer?.bottom > element?.bottom) {
          left = [...left, p];
          leftHeight += element?.height;
        } else {
          right = [...right, p];
          rightHeight += element?.height;
        }
      });
      if (rightHeight > leftHeight) {
        const half = Math.ceil(currParameters?.length / 2);
        setLeftSide(currParameters?.slice(0, half));
        setRightSide(currParameters?.slice(-half));
      } else {
        setLeftSide(left);
        setRightSide(right);
      }
      dispatch(hideLoading());
    }
    // eslint-disable-next-line
  }, [currParameters]);

  useEffect(() => {
    createColumns();
    // eslint-disable-next-line
  }, [paramLoading, currentTab]);

  useResizeObserver({
    ref: sectionRef,
    callback: createColumns,
    domRectProperty: 'width',
  });

  return (
    <ShadowBox className={classes.Container} ref={sectionRef}>
      {!loading && !paramLoading ? (
        <>
          <h5>{t('device_parameters:parameters')}</h5>
          <ParameterTabs
            currentTab={currentTab}
            onChange={(v) => setCurrentTab(v)}
            tabs={parameterCategories?.map((cat, i) => {
              return {
                value: i,
                label: cat.label,
              };
            })}
          />
          <section>
            <div>
              {leftSide?.map((item) => (
                <Parameter
                  key={item.key}
                  parameter={item}
                  parameterDetails={parameters?.find(
                    (p) => p.parameterIdRcu === item.id
                  )}
                />
              ))}
              <div ref={hiddenRef} className={classes.Placeholder}>
                {currParameters?.map((item) => (
                  <Parameter
                    id={item.key}
                    key={item.key}
                    parameter={item}
                    parameterDetails={parameters?.find(
                      (p) => p.parameterIdRcu === item.id
                    )}
                  />
                ))}
              </div>
            </div>
            <span />
            <div>
              {rightSide?.map((item) => (
                <Parameter
                  key={item.key}
                  parameter={item}
                  parameterDetails={parameters?.find(
                    (p) => p.parameterIdRcu === item.id
                  )}
                />
              ))}
            </div>
          </section>
        </>
      ) : (
        <div className={classes.Loading}>
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="text" />
          <div>
            <Skeleton animation="wave" variant="rectangular" height={340} />
            <Skeleton animation="wave" variant="rectangular" height={340} />
          </div>
        </div>
      )}
    </ShadowBox>
  );
};

export default DeviceParameters;
